import { deleteData, getData, postData, putData } from "../api/api";

export type PlatformUser = {
  userId: string;
  username: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  enabled: boolean;
  emailVerified: boolean;
  tenantId?: string;
  workspaceName?: string;
  /** Created by an external system through the integration API. */
  provisionedByIntegration?: boolean;
};

export type PlatformInvite = {
  id: string;
  workspaceId: string;
  workspaceName?: string;
  email: string;
  role: string;
  expiresAt?: string;
  acceptedAt?: string;
  invitedBy?: string;
  status: "PENDING" | "ACCEPTED" | "EXPIRED" | string;
  createdDate?: string;
};

export type PlatformStats = {
  totalUsers: number;
  activeUsers: number;
  totalWorkspaces: number;
  /** Workspaces using the BEAS LLM or BEAS store (Free quotas apply). */
  beasWorkspaces: number;
  /** Workspaces on their own LLM and sources only. */
  ownResourceWorkspaces: number;
  customQuotaWorkspaces: number;
  activeSupportByPackage: Record<string, number>;
  supportRevenueThisMonth: number;
  currency: string;
  supportExpiringIn30Days: number;
};

export type PlatformOpensearchTemplate = {
  templateKey?: string;
  serviceUrl?: string;
  adminUsername?: string;
  passwordConfigured: boolean;
  sslInsecure: boolean;
  enabled: boolean;
  filterAdByBackendRoles: boolean;
  configured: boolean;
};

export type PlatformOpensearchTemplatePayload = {
  serviceUrl: string;
  adminUsername: string;
  adminPassword?: string;
  sslInsecure?: boolean;
  enabled?: boolean;
  filterAdByBackendRoles?: boolean;
};

const BASE = "/admin";

export const platformAdminService = {
  listUsers: (search?: string, first = 0, max = 100) => {
    const params = new URLSearchParams();
    if (search?.trim()) params.set("search", search.trim());
    params.set("first", String(first));
    params.set("max", String(max));
    return getData(`${BASE}/users?${params}`) as Promise<PlatformUser[]>;
  },
  setUserEnabled: (userId: string, enabled: boolean) =>
    putData(`${BASE}/users/${encodeURIComponent(userId)}/enabled`, { enabled }) as Promise<PlatformUser>,
  deleteUser: (userId: string) =>
    deleteData(`${BASE}/users/${encodeURIComponent(userId)}`) as Promise<void>,
  listInvites: () => getData(`${BASE}/invites`) as Promise<PlatformInvite[]>,
  platformStats: () => getData(`${BASE}/platform-stats`) as Promise<PlatformStats>,
  getBeasOpensearchTemplate: () =>
    getData(`${BASE}/platform-datasources/opensearch-beas`) as Promise<PlatformOpensearchTemplate>,
  upsertBeasOpensearchTemplate: (payload: PlatformOpensearchTemplatePayload) =>
    putData(
      `${BASE}/platform-datasources/opensearch-beas`,
      payload
    ) as Promise<PlatformOpensearchTemplate>,
  testBeasOpensearchTemplate: (payload: PlatformOpensearchTemplatePayload) =>
    postData(`${BASE}/platform-datasources/opensearch-beas/test`, payload) as Promise<void>,
  listLlmModels: () =>
    getData(`${BASE}/platform-datasources/llm-models`) as Promise<PlatformLlmModel[]>,
  createLlmModel: (payload: PlatformLlmModelPayload) =>
    postData(`${BASE}/platform-datasources/llm-models`, payload) as Promise<PlatformLlmModel>,
  updateLlmModel: (id: string, payload: PlatformLlmModelPayload) =>
    putData(
      `${BASE}/platform-datasources/llm-models/${encodeURIComponent(id)}`,
      payload
    ) as Promise<PlatformLlmModel>,
  deleteLlmModel: (id: string) =>
    deleteData(`${BASE}/platform-datasources/llm-models/${encodeURIComponent(id)}`) as Promise<void>,
  testLlmModel: (id: string, payload?: Partial<PlatformLlmModelPayload>) =>
    postData(
      `${BASE}/platform-datasources/llm-models/${encodeURIComponent(id)}/test`,
      payload || {}
    ) as Promise<void>,
  getUiDocsStatus: () =>
    getData(`${BASE}/platform-ui-docs`) as Promise<PlatformUiDocsSyncStatus>,
  syncUiDocs: () =>
    postData(`${BASE}/platform-ui-docs/sync`, {}) as Promise<PlatformUiDocsSyncStatus>,
};

export type PlatformUiDocsSyncStatus = {
  indexName?: string;
  lastSyncedAt?: string | null;
  documentCount: number;
  chunkCount: number;
  classpathDocumentCount: number;
  lastError?: string | null;
  syncedBy?: string | null;
  ready: boolean;
};

export type PlatformLlmModel = {
  id: string;
  name: string;
  modelId: string;
  baseUrl: string;
  apiKeyConfigured: boolean;
  provider: string;
  beasResourceEnabled: boolean;
  enabled: boolean;
  sortOrder: number;
};

export type PlatformLlmModelPayload = {
  name: string;
  modelId: string;
  baseUrl: string;
  apiKey?: string;
  provider?: string;
  beasResourceEnabled?: boolean;
  enabled?: boolean;
  sortOrder?: number;
};
